import { useEffect, useRef } from 'react';

import styled from 'styled-components';

const Canvas = styled.canvas`
  display: block;
  width: 100%;
  height: 60%;
`;

function Animation() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width: number;
    let height: number;
    const cellSize = 20;
    const letters = 'BSTN';
    const grid: {
      x: number;
      y: number;
      char: string;
      visible: boolean;
      jitterX: number;
      jitterY: number;
    }[] = [];
    const shapes: { x: number; y: number; radius: number; duration: number }[] = [];

    const mouse = { x: -9999, y: -9999 };
    let hoverAlpha = 0;
    let mouseInsideCanvas = false;
    let rippleOffset = 0;

    function resize() {
      if (!canvas) return;
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width;
      canvas.height = height;
      initGrid();
    }

    function initGrid() {
      grid.length = 0;
      const cols = Math.ceil(width / cellSize);
      const rows = Math.ceil(height / cellSize);
      for (let y = 0; y < rows; y += 1) {
        for (let x = 0; x < cols; x += 1) {
          const char = letters[Math.floor(Math.random() * letters.length)];
          grid.push({
            x,
            y,
            char,
            visible: Math.random() > 0.5,
            jitterX: Math.random(),
            jitterY: Math.random(),
          });
        }
      }
    }

    function draw() {
      ctx.clearRect(0, 0, width, height);
      ctx.font = '12px monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      for (const cell of grid) {
        const rippleX =
          Math.sin((cell.x + rippleOffset + Math.sin(cell.y * 0.5)) * 0.15 + cell.jitterX * 0.1) *
          6;
        const rippleY =
          Math.cos((cell.y + rippleOffset + Math.sin(cell.x * 0.3)) * 0.3 + cell.jitterY * 0.1) *
          1.5;
        const py = cell.y * cellSize + cellSize / 2 + rippleY;
        const pxOffset = rippleX;
        const px = cell.x * cellSize + cellSize / 2 + pxOffset;

        const dx = mouse.x - px;
        const dy = mouse.y - py;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const maxDist = 100;
        const rawIntensity = Math.max(0, 1 - dist / maxDist);
        const intensity = rawIntensity * hoverAlpha;

        const baseR = 225;
        const baseG = 234;
        const baseB = 221;
        const targetR = 25;
        const targetG = 231;
        const targetB = 110;
        const r = Math.floor(baseR + (targetR - baseR) * intensity);
        const g = Math.floor(baseG + (targetG - baseG) * intensity);
        const b = Math.floor(baseB + (targetB - baseB) * intensity);
        ctx.fillStyle = `rgb(${r},${g},${b})`;

        const scale = 1 + 0.5 * intensity;
        ctx.save();
        ctx.translate(px, py);
        ctx.scale(scale, scale);
        if (cell.visible) ctx.fillText(cell.char, 0, 0);
        ctx.restore();
      }
    }

    function update() {
      if (Math.random() < 0.05) {
        shapes.push({
          x: Math.floor(Math.random() * (width / cellSize)),
          y: Math.floor(Math.random() * (height / cellSize)),
          radius: 3 + Math.floor(Math.random() * 4),
          duration: 60,
        });
      }

      for (let i = shapes.length - 1; i >= 0; i -= 1) {
        shapes[i].duration -= 1;
        if (shapes[i].duration <= 0) shapes.splice(i, 1);
      }

      for (let i = 0; i < grid.length * 0.02; i += 1) {
        const index = Math.floor(Math.random() * grid.length);
        grid[index].char = letters[Math.floor(Math.random() * letters.length)];
        grid[index].visible = Math.random() > 0.5;
        grid[index].jitterX = Math.random();
        grid[index].jitterY = Math.random();

        for (const shape of shapes) {
          const dx = grid[index].x - shape.x;
          const dy = grid[index].y - shape.y;
          if (dx * dx + dy * dy <= shape.radius * shape.radius) {
            grid[index].visible = true;
          }
        }
      }
    }

    function animate() {
      rippleOffset += 0.1;
      const isInView = mouseInsideCanvas;
      if (isInView && hoverAlpha < 1) {
        hoverAlpha += 0.05;
        if (hoverAlpha > 1) hoverAlpha = 1;
      } else if (!isInView && hoverAlpha > 0) {
        hoverAlpha -= 0.05;
        if (hoverAlpha < 0) hoverAlpha = 0;
      }
      update();
      draw();
      requestAnimationFrame(animate);
    }

    function handleMouseMove(e: MouseEvent) {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouseInsideCanvas = true;
    }

    function handleMouseLeave() {
      mouseInsideCanvas = false;
    }

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    resize();
    animate();

    return () => {
      resizeObserver.disconnect();
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return <Canvas ref={canvasRef} />;
}

export default Animation;
